import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Phone, Star, Leaf } from 'lucide-react';
import SEO from '../components/SEO';
import contactData from '../data/contact.json';

const Home = () => {
  const features = [
    {
      icon: <Star className="text-[#F7E39A]" size={40} />,
      title: '100% Steak Döner',
      description: 'Kein Hackfleisch! Nur hochwertiges Steak-Fleisch für besten Geschmack'
    },
    {
      icon: <Leaf className="text-[#A7C957]" size={40} />,
      title: 'Umweltfreundlich & Bio',
      description: 'Bio-Produkte, keine Plastikfolien – für Sie und unsere Umwelt'
    },
    {
      icon: <Leaf className="text-[#A7C957]" size={40} />,
      title: 'Veggie & Vegan',
      description: 'Grosse Auswahl an vegetarischen und veganen Optionen'
    }
  ];

  const menuHighlights = [
    { name: 'Dürüm', price: 'CHF 13.50', category: 'Der Klassiker' },
    { name: 'Falafel', price: 'CHF 12.50', category: 'Vegetarian' },
    { name: 'Chicken Wrap', price: 'CHF 13.50', category: 'Grilled Chicken' },
    { name: 'Planted', price: 'CHF 14.00', category: 'Vegetarian' }
  ];

  return (
    <div className="pt-20">
      <SEO 
        title="Ali Baba Kebab & Vegie - Bester Kebab in Sarnen | Frische Spezialitäten"
        description="Authentischer Kebab, Döner, Falafel & vegetarische Spezialitäten in Sarnen. Frisch zubereitet mit besten Zutaten. Takeaway & Dine-in. Grundacherweg 1, 6060 Sarnen. ☎️ 041 660 29 29"
        keywords="Kebab Sarnen, Döner Sarnen, Falafel Sarnen, Restaurant Sarnen, Takeaway Obwalden, Ali Baba Kebab"
        image="https://customer-assets.emergentagent.com/job_74889022-3a14-4f9f-9a7b-69c3c5635b4b/artifacts/bkldf31x_Alibaba%20Kebab%20Hero%20Photos.webp"
        path="/"
      />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_74889022-3a14-4f9f-9a7b-69c3c5635b4b/artifacts/bkldf31x_Alibaba%20Kebab%20Hero%20Photos.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1917]/60 via-[#1C1917]/50 to-[#1C1917]/80"></div>
        <div className="relative z-10 text-center px-4 max-w-4xl">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6 tracking-wider drop-shadow-lg">
            AUTHENTISCHER GESCHMACK
          </h1>
          <p className="text-xl md:text-2xl text-[#F7E39A] mb-4 font-light">aus Sarnen</p>
          <p className="text-lg text-[#E3E3E3] mb-8 max-w-2xl mx-auto">
            100% Steak Döner – kein Hackfleisch! Umweltfreundlich mit Bio-Produkten und ohne Plastikfolien. Entdecken Sie unsere frisch zubereiteten Spezialitäten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/menu" className="btn-primary inline-block">Menü entdecken</Link>
            <a href="tel:+41416602929" className="btn-secondary inline-block">Jetzt anrufen</a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section bg-[#1C1917]">
        <div className="container mx-auto">
          <h2 className="text-5xl text-center text-white mb-16">Warum Ali Baba?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-[#2A2725] p-8 rounded-lg text-center transition-transform hover:scale-105"
                style={{ border: '1px solid rgba(247, 227, 154, 0.2)' }}
              >
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-2xl text-[#F7E39A] mb-3">{feature.title}</h3>
                <p className="text-[#E3E3E3]">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Detailed Content */}
          <div className="max-w-5xl mx-auto space-y-12">
            <div className="bg-[#0F0E0D] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.1)' }}>
              <h3 className="text-3xl text-[#F7E39A] mb-6">100% Steak Döner – Qualität, die man schmeckt</h3>
              <div className="text-[#E3E3E3] space-y-4 leading-relaxed">
                <p>
                  Bei Ali Baba in Sarnen verwenden wir ausschliesslich hochwertiges Steak-Fleisch – kein Hackfleisch. Der Unterschied ist deutlich: festere Konsistenz,
                   intensiverer Geschmack und saftige Textur. Jede Scheibe wird frisch vom Spiess geschnitten und behält ihre natürliche Struktur.
                </p>
                <p>
                  Unser Steak-Döner wird nach traditionellen Methoden zubereitet: sorgfältig mariniert, geschichtet und langsam am vertikalen Grill gegart.
                   Die äusseren Schichten werden knusprig, während das Innere saftig bleibt. Das ist kulinarische Handwerkskunst, die Sie schmecken werden.
                </p>
              </div>
            </div>

            <div className="bg-[#0F0E0D] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.1)' }}>
              <h3 className="text-3xl text-[#F7E39A] mb-6">Umweltfreundlich & Bio – Verantwortung für morgen</h3>
              <div className="text-[#E3E3E3] space-y-4 leading-relaxed">
                <p>
                  Nachhaltigkeit ist bei Ali Baba gelebte Praxis. Um unseren Beitrag zum Umweltschutz zu leisten, vermeiden wir bewusst Verpackungsmaterialien wie Plastiksäcke und Alufolie. Unsere Takeaway-Verpackungen sind biologisch abbaubar und belasten die Umwelt nicht.
                </p>
                <p>
                  Wir beziehen unser Gemüse saisonabhängig bevorzugt von einheimischen Lieferanten. Frisches Gemüse, Salate und Kräuter werden täglich geliefert und verarbeitet. Wir achten auf faire Anbaubedingungen und minimieren Lebensmittelverschwendung. Wenn Sie bei uns essen, unterstützen Sie ein lokales Unternehmen in Sarnen, das Genuss und Umweltschutz vereint.
                </p>
              </div>
            </div>

            <div className="bg-[#0F0E0D] p-8 rounded-lg" style={{ border: '1px solid rgba(247, 227, 154, 0.1)' }}>
              <h3 className="text-3xl text-[#F7E39A] mb-6">Veggie & Vegan – Vielfalt für jeden Geschmack</h3>
              <div className="text-[#E3E3E3] space-y-4 leading-relaxed">
                <p>
                  Neben unseren Fleischgerichten bieten wir auch eine Auswahl an wertvollen vegetarischen und veganen Speisen an. Unsere Falafel werden täglich frisch zubereitet – knusprig von aussen, saftig von innen. Dazu servieren wir hausgemachten Hummus, der cremig und aromatisch ist.
                </p>
                <p>
                  Besonders stolz sind wir auf unser Planted-Angebot – eine innovative Fleischalternative aus der Schweiz. Auch unser gegrillter Halloumi-Käse, Çiğ Köfte und Gemüsekebap sind Highlights. Unsere vegetarischen Gerichte sind vollwertige Mahlzeiten, die mit derselben Sorgfalt zubereitet werden wie unsere Fleischgerichte. In Sarnen sind wir eine Top-Adresse für pflanzliche Küche.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-16 bg-[#2A2725]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="relative overflow-hidden rounded-lg group">
              <img 
                src="/media/gallery/taschen.webp" 
                alt="Frische Döner Zubereitung" 
                className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 to-transparent flex items-end">
                <p className="text-white text-lg font-semibold p-6">Frisch zubereitet mit Liebe</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-lg group">
              <img 
                src="/media/gallery/Falafel.webp" 
                alt="Vegetarische Spezialitäten" 
                className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 to-transparent flex items-end">
                <p className="text-white text-lg font-semibold p-6">Veggie & Vegan Optionen</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Preview */}
      <section className="section bg-[#0F0E0D]">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl text-white mb-4">Unsere Favoriten</h2>
            <p className="text-[#E3E3E3] text-lg">Eine kleine Auswahl aus unserem vielfältigen Angebot</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {menuHighlights.map((item, index) => (
              <div 
                key={index} 
                className="bg-[#1C1917] p-6 rounded-lg transition-transform hover:scale-105"
                style={{ border: '1px solid rgba(247, 227, 154, 0.3)' }}
              >
                <span className="text-[#A7C957] text-sm font-semibold">{item.category}</span>
                <h3 className="text-xl text-white mt-2 mb-3">{item.name}</h3>
                <p className="text-[#F7E39A] text-2xl font-bold">{item.price}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/menu" className="btn-primary">Komplettes Menü ansehen</Link>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="section bg-[#1C1917]">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-[#2A2725] p-8 rounded-lg text-center">
              <Clock className="text-[#F7E39A] mx-auto mb-4" size={48} />
              <h3 className="text-2xl text-white mb-4">Öffnungszeiten</h3>
              <p className="text-[#E3E3E3]">{contactData.hours.weekdays.days}:</p>
              <p className="text-[#E3E3E3]">{contactData.hours.weekdays.lunch.open} - {contactData.hours.weekdays.lunch.close}</p>
              <p className="text-[#E3E3E3]">{contactData.hours.weekdays.dinner.open} - {contactData.hours.weekdays.dinner.close}</p>
              <p className="text-[#E3E3E3] mt-2">{contactData.hours.weekend.days}: {contactData.hours.weekend.open} - {contactData.hours.weekend.close}</p>
            </div>
            <div className="bg-[#2A2725] p-8 rounded-lg text-center">
              <MapPin className="text-[#F7E39A] mx-auto mb-4" size={48} />
              <h3 className="text-2xl text-white mb-4">Standort</h3>
              <p className="text-[#E3E3E3]">{contactData.restaurant.address.street}</p>
              <p className="text-[#E3E3E3]">{contactData.restaurant.address.zip} {contactData.restaurant.address.city}, {contactData.restaurant.address.country}</p>
            </div>
            <div className="bg-[#2A2725] p-8 rounded-lg text-center">
              <Phone className="text-[#F7E39A] mx-auto mb-4" size={48} />
              <h3 className="text-2xl text-white mb-4">Reservierung</h3>
              <a href={`tel:${contactData.restaurant.phone}`} className="text-[#F7E39A] text-xl font-semibold hover:text-white transition-colors">
                {contactData.restaurant.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#F7E39A] text-[#1C1917]">
        <div className="container mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6">Bereit für echten Geschmack?</h2>
          <p className="text-xl mb-8">Besuchen Sie uns in Sarnen oder bestellen Sie telefonisch</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="bg-[#1C1917] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-transform hover:scale-105"
            >
              Kontakt aufnehmen
            </Link>
            <Link 
              to="/menu" 
              className="bg-transparent border-2 border-[#1C1917] text-[#1C1917] px-8 py-4 rounded-lg font-semibold text-lg transition-all hover:bg-[#1C1917] hover:text-white"
            >
              Menü ansehen
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
